import React, { useEffect, useMemo, useState } from 'react';
import { Card, Divider, Typography, Button } from 'antd';
import {
  CheckCircleOutlined,
  SunOutlined,
  EnvironmentOutlined,
  CloseCircleFilled,
  LoadingOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';
import { Drawer } from '../../common/Drawer';
import {
  cancelBookingSlot,
  getBookingDetailsRequest,
} from '../../../api/requests/bookings';
import { COLORS, FONT_WEIGHTS, TEXT_SIZES } from '../../../utils/constants';
import { convertUtcToLocal } from '../../../utils/dateUtils';
import DummyFacilityImage from '../../../assets/dummyFacilityImage.png';
import { useSnackbar } from '../../../contexts/SnackbarContext/SnackbarProvider';
import Modal from '../../common/Modal';
import Spinner from '../../common/Spinner';
import { SlotCategoryEnum } from '../../../enums/enums';
import { getSlotCategory } from '../../../utils/utils';
import {
  BookingDetailsType,
  BookingSlotType,
  DateGroup,
} from '../../../types/types';

interface Props {
  open: boolean;
  onClose: () => void;
  bookingId: number;
  cancelSlot: boolean;
  onSlotCancelled?: () => void;
}

const ViewBookingDrawer: React.FC<Props> = ({
  open,
  onClose,
  bookingId,
  cancelSlot,
  onSlotCancelled,
}) => {
  const snackbar = useSnackbar();
  const [loading, setLoading] = useState<boolean>(false);
  const [bookingDetails, setBookingDetails] =
    useState<BookingDetailsType | null>(null);
  const [isCancelSlotOpen, setIsCancelSlotOpen] = useState<boolean>(false);
  const [selectedSlot, setSelectedSlot] = useState<BookingSlotType | null>(
    null,
  );
  const [isSlotCanceling, setIsSlotCanceling] = useState<boolean>(false);

  const openCancelSlotModal = (slot: BookingSlotType) => {
    setSelectedSlot(slot);
    setIsCancelSlotOpen(true);
  };

  const closeCancelSlotModal = () => {
    setSelectedSlot(null);
    setIsCancelSlotOpen(false);
  };

  const fetchBookingDetails = async () => {
    if (!bookingId) return;
    try {
      setLoading(true);
      const response = await getBookingDetailsRequest(bookingId);
      setBookingDetails(response?.value || null);
    } catch (err) {
      console.log('err', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) fetchBookingDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const groupedSlots: DateGroup[] = useMemo(() => {
    if (!bookingDetails?.bookingSlots) return [];
    const slots = bookingDetails.bookingSlots as BookingSlotType[];
    //  Group by DATE
    const dateMap: Record<string, BookingSlotType[]> = {};
    slots.forEach((slot) => {
      const date = convertUtcToLocal(slot.startTime, 'MMM D, YYYY');
      if (!dateMap[date]) dateMap[date] = [];
      dateMap[date].push(slot);
    });
    //  Group by CATEGORY (FIXED TYPE)
    return Object.keys(dateMap).map((date) => {
      const categoryMap: Partial<Record<SlotCategoryEnum, BookingSlotType[]>> =
        {};
      dateMap[date].forEach((slot) => {
        const category = getSlotCategory(slot.startTime);
        if (!categoryMap[category]) {
          categoryMap[category] = [];
        }
        categoryMap[category]!.push(slot);
      });

      return {
        date,
        categories: Object.entries(categoryMap).map(([category, slots]) => ({
          category: category as SlotCategoryEnum,
          slots: slots as BookingSlotType[],
        })),
      };
    });
  }, [bookingDetails]);

  if (!bookingDetails) return null;

  const handleCancelSlot = async () => {
    if (!selectedSlot) return;
    try {
      setIsSlotCanceling(true);
      const response = await cancelBookingSlot({
        bookingId: bookingId,
        slotId: selectedSlot.slotId,
      });
      if (response?.isSuccess) {
        setSelectedSlot(null);
        snackbar.success('Slot cancelled successfully');
        await fetchBookingDetails();
        onSlotCancelled?.();
      } else {
        snackbar.error(response?.error);
      }
    } catch (error: any) {
      snackbar.error(error?.Error || 'Error cancelling slot');
    } finally {
      setIsSlotCanceling(false);
    }
  };

  return (
    <Drawer title="View Booking" open={open} onClose={onClose} width="40vw">
      {loading ? (
        <SpinnerWrapper>
          <Spinner />
        </SpinnerWrapper>
      ) : (
        <>
          <DetailsCard>
            <VenueSection>
              <VenueImage
                src={
                  bookingDetails?.bookingSlots?.[0].facilityImageUrl ??
                  DummyFacilityImage
                }
                alt={'Facility'}
              />
              <VenueInfo>
                <VenueName level={5}>
                  {bookingDetails?.bookingSlots?.[0].facilityName}
                </VenueName>
                <VenueAddress>
                  <EnvironmentOutlined />
                  <span>
                    {bookingDetails?.bookingSlots?.[0].facilityAddress}
                  </span>
                </VenueAddress>
                <VenueSport>
                  {bookingDetails?.bookingSlots?.[0].sportName}
                </VenueSport>
              </VenueInfo>
            </VenueSection>
            <Divider />
            <InfoContainer>
              <InfoRow>
                <Info>Booking ID</Info>
                <Info>{bookingDetails.bookingReference}</Info>
              </InfoRow>
              <InfoRow>
                <Info>Booking Date & Time</Info>
                <Info>{convertUtcToLocal(bookingDetails.bookingDate)}</Info>
              </InfoRow>
              <InfoRow>
                <Info>Booking Status</Info>
                <Info>{bookingDetails.statusName}</Info>
              </InfoRow>
            </InfoContainer>
          </DetailsCard>
          <SlotTitle level={5}>
            Slot Details ({bookingDetails.bookingSlots.length})
          </SlotTitle>
          <SlotsWrapper>
            {groupedSlots.map((group) => (
              <div key={group.date}>
                {group.categories.map((cat) => (
                  <div key={cat.category}>
                    <SlotHeader>
                      <Left>
                        <SunIcon />
                        <Text strong>{cat.category}</Text>
                      </Left>
                      <Text type="secondary">{group.date}</Text>
                    </SlotHeader>
                    <GroupSlotsWrapper>
                      {cat.slots.map((slot, idx) => (
                        <DetailsCard key={idx}>
                          <SlotRow>
                            <div>
                              <Time>
                                {convertUtcToLocal(slot.startTime, 'hh:mm A')} -{' '}
                                {convertUtcToLocal(slot.endTime, 'hh:mm A')}
                                {slot.paymentLineItemInfo?.isCancellable && (
                                  <CheckIcon />
                                )}
                              </Time>
                              <Text type="secondary">
                                {slot.courtName} · ₹{slot.slotPrice}
                              </Text>
                            </div>
                            {slot.paymentLineItemInfo?.isCancellable ===
                              false && (
                              <CancelledWrapper>
                                <CancelledRow>
                                  <CancelledIcon />
                                  <CancelledText>Cancelled</CancelledText>
                                </CancelledRow>
                                <CancelledDate>
                                  {slot.paymentLineItemInfo?.cancelledDate &&
                                    convertUtcToLocal(
                                      slot.paymentLineItemInfo.cancelledDate,
                                      'DD MMM YYYY, hh:mm A',
                                    )}
                                </CancelledDate>
                              </CancelledWrapper>
                            )}
                            {slot.paymentLineItemInfo?.isCancellable === true &&
                              cancelSlot &&
                              bookingDetails.status === 2 && (
                                <CancelButton
                                  onClick={() => openCancelSlotModal(slot)}
                                >
                                  Cancel
                                </CancelButton>
                              )}
                          </SlotRow>
                        </DetailsCard>
                      ))}
                    </GroupSlotsWrapper>
                  </div>
                ))}
              </div>
            ))}
          </SlotsWrapper>
          {isCancelSlotOpen && selectedSlot && (
            <Modal
              open={!!isCancelSlotOpen}
              title="Cancel Slot"
              onCancel={closeCancelSlotModal}
              headerBgColor={COLORS.red.primary}
              footerAlign="end"
              minHeight="auto"
              footer={
                <>
                  <Button
                    key="cancel"
                    onClick={closeCancelSlotModal}
                    disabled={isSlotCanceling}
                  >
                    Go back
                  </Button>
                  <Button
                    key="submit"
                    type="primary"
                    danger
                    onClick={handleCancelSlot}
                    disabled={isSlotCanceling}
                  >
                    Cancel slot
                    {isSlotCanceling && <StyledLoadingOutlined spin />}
                  </Button>
                </>
              }
            >
              <span>Are you sure you want to cancel this slot?</span>
            </Modal>
          )}
        </>
      )}
    </Drawer>
  );
};

export default ViewBookingDrawer;

/* -- STYLES -- */

const { Text } = Typography;

const SpinnerWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 60vh;
`;

const DetailsCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  &&& .ant-card-body {
    padding: 12px;
  }
`;

const VenueSection = styled.div`
  display: grid;
  grid-template-columns: 90px 1fr;
  gap: 12px;
  align-items: center;
`;

const VenueImage = styled.img`
  width: 90px;
  height: 90px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
`;

const VenueInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const VenueName = styled(Typography.Title)`
  color: ${COLORS.black.primary};
  font-size: ${TEXT_SIZES.SM}px;
  font-weight: ${FONT_WEIGHTS.MEDIUM};
  && {
    margin-bottom: 4px;
  }
`;

const VenueAddress = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${COLORS.grey.primary};
  font-size: ${TEXT_SIZES.XS}px;
  font-weight: ${FONT_WEIGHTS.REGULAR};
  & img {
    color: ${COLORS.black.primary};
    font-size: ${TEXT_SIZES.XL}px;
  }
`;

const VenueSport = styled.div`
  margin-top: 6px;
  color: ${COLORS.skyBlue.primary};
  font-weight: ${FONT_WEIGHTS.MEDIUM};
  font-size: ${TEXT_SIZES.SM}px;
`;

const InfoContainer = styled.div`
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  background-color: ${COLORS.skyBlue.secondary};
  padding: 4px 8px;
`;

const Info = styled(Typography.Text)`
  color: ${COLORS.black.primary};
  font-size: ${TEXT_SIZES.XS}px;
  font-weight: ${FONT_WEIGHTS.REGULAR};
`;

const SlotTitle = styled(Typography.Title)`
  color: ${COLORS.black.primary};
  font-weight: ${FONT_WEIGHTS.BOLD};
  margin-top: 16px;
`;

const SlotsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const GroupSlotsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SlotHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 16px 0 8px;
`;

const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SlotRow = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Time = styled.div`
  display: flex;
  gap: 6px;
`;

const SunIcon = styled(SunOutlined)`
  color: ${COLORS.orange.primary};
`;

const CheckIcon = styled(CheckCircleOutlined)`
  color: ${COLORS.blue.primary};
`;

const CancelledRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  margin-top: 4px;
`;

const CancelledIcon = styled(CloseCircleFilled)`
  color: ${COLORS.red.primary};
  font-size: ${TEXT_SIZES.MD}px;
`;

const CancelledText = styled(Typography.Text)`
  color: ${COLORS.red.primary};
  font-weight: ${FONT_WEIGHTS.MEDIUM};
`;

const CancelledDate = styled(Typography.Text)`
  font-size: ${TEXT_SIZES.XS}px;
  color: ${COLORS.grey.primary};
`;

const CancelButton = styled(Button)`
  min-width: 80px;
  height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  font-size: ${TEXT_SIZES.SM}px;
  font-weight: ${FONT_WEIGHTS.MEDIUM};
  color: ${COLORS.red.primary};
  background: ${COLORS.white.primary};
  border: 0.5px solid ${COLORS.red.primary};

  &:hover,
  &:focus,
  &:active {
    color: ${COLORS.red.primary} !important;
    background: ${COLORS.white.primary} !important;
    border-color: ${COLORS.red.primary} !important;
  }
`;

const CancelledWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledLoadingOutlined = styled(LoadingOutlined)`
  margin-left: 4px;
`;
